import { RobotOutlined } from '@ant-design/icons';
import { Button, Form, Input, message } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import axios from '../api';
import { getMediaUrl } from '../config';
import { getDefaultRouteForUser } from '../utils/authRedirect';

const Login = () => {
  const [captcha, setCaptcha] = useState({ image_url: '', captcha_id: '', captcha_key: '' });
  const [loading, setLoading] = useState(false);
  const [captchaError, setCaptchaError] = useState(false);
  const canvasRef = useRef(null);

  // 粒子动画效果
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = [];
    const particleCount = 50;

    // 创建粒子
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        size: Math.random() * 2 + 1,
        opacity: Math.random() * 0.5 + 0.2
      });
    }

    // 动画循环
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach(particle => {
        particle.x += particle.vx;
        particle.y += particle.vy;

        if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1;

        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(90, 125, 27, ${particle.opacity})`;
        ctx.fill();
      });

      requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 单次获取验证码
  const fetchCaptcha = async (showErrorOnFail = false) => {
    setCaptchaError(false);
    try {
      const res = await axios.get('/api/captcha/');
      // 兼容新旧字段名
      const captchaData = {
        captcha_id: res.data.captcha_id || res.data.captcha_key,
        captcha_key: res.data.captcha_id || res.data.captcha_key,  // 兼容旧字段名
        image_url: getMediaUrl(res.data.image_url || res.data.imageUrl),
      };
      setCaptcha(captchaData);
      return true;
    } catch (err) {
      if (showErrorOnFail) {
        setCaptchaError(true);
        // 判断是否为后端服务不可用
        const isBackendDown = !err.response || [504, 503, 0].includes(err.response?.status) || err.code === 'ERR_NETWORK';
        
        // 根据环境显示不同的错误提示
        const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        let errorMessage = '验证码加载失败';
        
        if (isBackendDown) {
          if (isDevelopment) {
            errorMessage = '后端未启动，请先运行 ./scripts/start_local.sh';
          } else {
            errorMessage = '无法连接到服务器，请检查网络连接或稍后重试';
          }
        } else if (err.response?.status === 500) {
          errorMessage = '服务器错误，请稍后重试';
        } else if (err.response?.status === 404) {
          errorMessage = '验证码接口不存在，请联系管理员';
        } else if (err.response?.data?.error) {
          errorMessage = err.response.data.error;
        } else if (err.response?.data?.detail) {
          errorMessage = err.response.data.detail;
        }
        
        message.warning({
          content: errorMessage,
          duration: 4
        });
      }
      return false;
    }
  };

  // 带自动重试的加载（最多 5 次，间隔 2 秒）
  const loadCaptchaWithRetry = async () => {
    const retries = 5;
    const interval = 2000;
    for (let i = 0; i < retries; i++) {
      if (await fetchCaptcha(i === retries - 1)) return;
      if (i < retries - 1) await new Promise((r) => setTimeout(r, interval));
    }
  };

  useEffect(() => {
    loadCaptchaWithRetry();
  }, []);

  // 登录提交
  const onFinish = async (values) => {
    setLoading(true);
    try {
      const captchaCode = (values.captcha || '').trim(); // 去除首尾空格，后端校验不区分大小写
      const res = await axios.post('/api/login/', {
        username: values.username,
        password: values.password,
        captcha_id: captcha.captcha_id || captcha.captcha_key,
        captcha_code: captchaCode,
        captcha_key: captcha.captcha_id || captcha.captcha_key,
        captcha_value: captchaCode,
      });
      const user = res.data.user;
      message.success({
        content: '登录成功',
        duration: 2,
        style: {
          marginTop: '20vh',
        },
        icon: '🎉',
        className: 'custom-toast',
      });
      setTimeout(() => {
        localStorage.setItem('token', res.data.access);
        localStorage.setItem('user_info', JSON.stringify(user));
        window.location.href = getDefaultRouteForUser(user);
      }, 1000);
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.detail || '登录失败';
      if (errorMsg.includes('验证码')) {
        message.error({
          content: errorMsg,
          duration: 2,
          style: {
            marginTop: '20vh',
            color: '#ff4d4f',
            fontSize: '16px',
            fontWeight: 'bold',
          },
        });
      } else {
        message.error({
          content: errorMsg,
          duration: 2,
          style: {
            marginTop: '20vh',
          },
        });
      }
      fetchCaptcha();
    }
    setLoading(false);
  };

  return (
    <div style={{
      position: 'relative',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      overflow: 'hidden'
    }}>
      {/* 动态背景 */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1
        }}
      />

      {/* 装饰性几何图形 */}
      <div style={{
        position: 'absolute',
        top: '10%',
        left: '10%',
        width: 200,
        height: 200,
        border: '2px solid rgba(255,255,255,0.1)',
        borderRadius: '50%',
        zIndex: 2
      }} />
      <div style={{
        position: 'absolute',
        top: '60%',
        right: '15%',
        width: 150,
        height: 150,
        border: '2px solid rgba(255,255,255,0.1)',
        borderRadius: '50%',
        zIndex: 2
      }} />

      {/* 主要内容 */}
      <div style={{
        position: 'relative',
        zIndex: 3,
        display: 'flex',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {/* 左侧品牌区域 */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          padding: '0 60px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: 40
          }}>
            <svg
              width="80"
              height="80"
              viewBox="0 0 38 38"
              style={{
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                marginRight: 24,
                display: 'block',
                border: '2px solid rgba(255,255,255,0.2)'
              }}
            >
              <circle cx="19" cy="19" r="22" fill="rgba(255,255,255,0.1)" />
              <circle cx="11" cy="19" r="8" fill="none" stroke="#fff" strokeWidth="4" />
              <circle cx="27" cy="19" r="8" fill="none" stroke="#fff" strokeWidth="4" />
            </svg>
            <div>
              <div style={{
                fontSize: 48,
                fontWeight: 700,
                letterSpacing: 2,
                fontFamily: 'Montserrat, Arial, sans-serif',
                marginBottom: 8
              }}>
                blooart
              </div>
              <div style={{
                fontSize: 36,
                fontWeight: 700,
                letterSpacing: 2,
                fontFamily: 'Montserrat, Arial, sans-serif',
                opacity: 0.9
              }}>
                CAMPUS
              </div>
            </div>
          </div>

          <h1 style={{
            fontSize: 48,
            fontWeight: 700,
            marginBottom: 20,
            textAlign: 'center',
            background: 'linear-gradient(45deg, #fff, #e0e0e0)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            智能教务系统
          </h1>

          <p style={{
            fontSize: 20,
            opacity: 0.8,
            textAlign: 'center',
            maxWidth: 500,
            lineHeight: 1.6
          }}>
            融合AI技术与现代教育理念，打造智能化、个性化的教育管理平台
          </p>

          {/* AI特性展示 */}
          <div style={{
            display: 'flex',
            gap: 40,
            marginTop: 60
          }}>
            <div style={{ textAlign: 'center' }}>
              <RobotOutlined style={{ fontSize: 32, marginBottom: 8, color: '#fff' }} />
              <div style={{ fontSize: 16, opacity: 0.8 }}>AI智能助手</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📚</div>
              <div style={{ fontSize: 16, opacity: 0.8 }}>知识库管理</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>☁️</div>
              <div style={{ fontSize: 16, opacity: 0.8 }}>云端同步</div>
            </div>
          </div>
        </div>

        {/* 右侧登录表单 */}
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 60px'
        }}>
          <div style={{
            width: 400,
            padding: 48,
            borderRadius: 20,
            background: 'rgba(255,255,255,0.1)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.2)',
            boxShadow: '0 25px 45px rgba(0,0,0,0.1)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: 40 }}>
              <h2 style={{
                fontSize: 32,
                fontWeight: 700,
                color: 'white',
                marginBottom: 8
              }}>
                欢迎回来
              </h2>
              <p style={{
                fontSize: 16,
                color: 'rgba(255,255,255,0.7)',
                margin: 0
              }}>
                请登录您的账户
              </p>
            </div>

            <Form onFinish={onFinish}>
              <Form.Item name="username" rules={[{ required: true, message: '请输入账号' }]}>
                <Input
                  placeholder="账号"
                  size="large"
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 12,
                    color: 'white',
                    fontSize: 16,
                    height: 50
                  }}
                />
              </Form.Item>
              <Form.Item name="password" rules={[{ required: true, message: '请输入密码' }]}>
                <Input.Password
                  placeholder="密码"
                  size="large"
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 12,
                    color: 'white',
                    fontSize: 16,
                    height: 50
                  }}
                />
              </Form.Item>
              <Form.Item style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <Form.Item name="captcha" noStyle rules={[{ required: true, message: '请输入验证码' }]}>
                    <Input
                      placeholder="验证码（不区分大小写）"
                      size="large"
                      autoComplete="off"
                      style={{
                        flex: 1,
                        minWidth: 180,
                        background: 'rgba(255,255,255,0.1)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: 12,
                        color: 'white',
                        fontSize: 18,
                        height: 52
                      }}
                    />
                  </Form.Item>
                  {captchaError ? (
                    <Button
                      size="large"
                      onClick={loadCaptchaWithRetry}
                      style={{
                        height: 50,
                        borderRadius: 12,
                        border: '1px solid rgba(255,255,255,0.2)',
                        background: 'rgba(255,255,255,0.1)',
                        color: 'white'
                      }}
                    >
                      重试
                    </Button>
                  ) : (
                    <img
                      src={captcha.image_url}
                      alt="验证码"
                      style={{
                        height: 50,
                        cursor: 'pointer',
                        borderRadius: 12,
                        border: '1px solid rgba(255,255,255,0.2)'
                      }}
                      onClick={() => fetchCaptcha(true)}
                      onError={() => {
                        if (captcha.image_url) {
                          fetchCaptcha(true);
                        }
                      }}
                      title="点击刷新验证码"
                    />
                  )}
                </div>
              </Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                style={{
                  width: '100%',
                  background: 'linear-gradient(45deg, #667eea, #764ba2)',
                  border: 'none',
                  fontWeight: 600,
                  letterSpacing: 4,
                  fontSize: 16,
                  height: 50,
                  borderRadius: 12,
                  boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)'
                }}
                loading={loading}
              >
                登 录
              </Button>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
